import { shareMessage, shareURL } from '@tma.js/sdk-react'
import { useMutation } from '@tanstack/react-query'
import { Share2 } from 'lucide-react'
import { toast } from 'sonner'
import { useMobileTrpc } from 'shared/api'
import { buildLotShareLink } from 'shared/lib/telegram'
import { useTranslate } from 'shared/lib/i18n'
import { cn } from 'shared/lib/utils'

/**
 * Shares a lot. Prefers a bot-authored rich message via Telegram's in-app chat
 * picker (`shareMessage`, Mini Apps v8.0+): the backend mints a prepared message
 * — lot photo, title + price, and an "open lot" button — and we hand its id to
 * `shareMessage`. Falls back to sharing the plain deep link (`shareURL`) and, off
 * Telegram entirely (browser admin, dev preview), to copying it.
 *
 * Renders nothing only when neither path can work: not inside Telegram AND no
 * link to copy ({@link buildLotShareLink} unconfigured).
 */
export function ShareLotButton({
	lotId,
	title,
	className,
}: {
	lotId: string
	title: string
	className?: string
}) {
	const { t } = useTranslate()
	const trpc = useMobileTrpc()
	// Read-only: minting a share message changes no data, so skip the global
	// query invalidation the shared MutationCache does after every mutation.
	const prepare = useMutation(
		trpc.lots.PrepareShare.mutationOptions({ meta: { skipInvalidate: true } }),
	)

	const link = buildLotShareLink(lotId)
	const canShareMessage = shareMessage.isAvailable()
	if (!link && !canShareMessage) return null

	function shareLink(): void {
		if (!link) return
		if (shareURL.ifAvailable(link, title).ok) return
		void navigator.clipboard?.writeText(link)
		toast.success(t('common.copied'))
	}

	async function handleShare(): Promise<void> {
		if (canShareMessage) {
			// Only a failure to MINT the message falls back to link sharing.
			let preparedMessageId: string | null = null
			try {
				;({ preparedMessageId } = await prepare.mutateAsync({ lotId }))
			} catch {
				preparedMessageId = null
			}
			if (preparedMessageId) {
				// Hand off to Telegram's native picker. Once it is open, the user
				// cancelling (which rejects with `prepared_message_failed`) or a
				// send error ends the interaction — falling back here would pop a
				// second share dialog right after they dismissed the first.
				await shareMessage(preparedMessageId).catch(() => {})
				return
			}
		}
		shareLink()
	}

	return (
		<button
			aria-label={t('pages.lot.share')}
			disabled={prepare.isPending}
			className={cn(
				'flex h-9 w-9 items-center justify-center rounded-full bg-black/65 text-white backdrop-blur-sm active:opacity-60 disabled:opacity-50',
				className,
			)}
			onClick={() => void handleShare()}
		>
			<Share2 className='h-5 w-5 -ml-0.75' />
		</button>
	)
}
